/**
 * The pipeline's stages, said the way the person reading them says them.
 *
 * The stage machine is the truth; this is its vocabulary, and there are two of
 * them, because the two sides of the desk do not see the same concept.
 *
 * The team (strategists and admins) sees every step, each with its own name:
 * a concept starts as a "New presentation" before any editor knows it exists,
 * becomes "Ready for editor" when a strategist releases it, is "In progress",
 * is "Delivered" back to the strategist, goes on to "Javid's review" or back to
 * the editor as "Needs revision", and ends "Approved".
 *
 * The editor sees five words and no more: "Not started", "In progress",
 * "Delivered", "Needs revision" (with its round), "Approved". The strategist's
 * check and the admin's final review are both "Delivered" from where they sit -
 * the work is out of their hands either way - and a new presentation never
 * reaches them at all (the server keeps those concepts from them; the map
 * below only has to be total).
 */
import {
  CheckCircle2Icon,
  CircleDashedIcon,
  EyeIcon,
  NotebookPenIcon,
  RotateCcwIcon,
  ShieldCheckIcon,
  TimerIcon,
  Trash2Icon,
  type LucideIcon,
} from "lucide-react";
import type { PipelineStage, Role } from "@scandi/shared";

/** Every status the portal can draw, in reading order. Each is also a `data-tone`. */
export const STATUSES = [
  "strategist",
  "corrections",
  "todo",
  "doing",
  "review",
  "admin",
  "done",
  "trash",
] as const;

export type Status = (typeof STATUSES)[number];

/** The statuses an editor ever meets. */
export const EDITOR_STATUSES = [
  "corrections",
  "todo",
  "doing",
  "review",
  "done",
  "trash",
] as const satisfies readonly Status[];

export type EditorStatus = (typeof EDITOR_STATUSES)[number];

/** Whose words to use: the editor's five, or the team's whole walk. */
export type Audience = "editor" | "team";

/** Anybody who is not an editor (a view-as editor is one) reads the team's words. */
export function audienceOf(role: Role | null): Audience {
  return role === "editor" ? "editor" : "team";
}

const TEAM_OF_STAGE: Record<PipelineStage, Status> = {
  strategist_review: "strategist",
  assigned: "todo",
  in_progress: "doing",
  in_review: "review",
  changes_requested: "corrections",
  pending_review: "admin",
  approved: "done",
  shipped: "done",
  cancelled: "trash",
};

const EDITOR_OF_STAGE: Record<PipelineStage, EditorStatus> = {
  // Never shown to an editor - a concept is invisible to them until released -
  // but the map must be total, and "not started" is the honest word for it.
  strategist_review: "todo",
  assigned: "todo",
  in_progress: "doing",
  in_review: "review",
  changes_requested: "corrections",
  // The admin's final review: from the editor's side the work is delivered and waiting.
  pending_review: "review",
  approved: "done",
  shipped: "done",
  cancelled: "trash",
};

/**
 * The status a stage reads as. The team's by default: the admin's activity log
 * and every screen across editors want the whole walk.
 */
export function statusOf(stage: PipelineStage, audience: Audience = "team"): Status {
  // A stage this build does not know (a row read mid-deploy) reads as the first one.
  return (audience === "editor" ? EDITOR_OF_STAGE[stage] : TEAM_OF_STAGE[stage]) ?? "todo";
}

/** The editor's word for a stage. */
export function editorStatusOf(stage: PipelineStage): EditorStatus {
  return EDITOR_OF_STAGE[stage] ?? "todo";
}

/**
 * What each status looks like: one icon, one tone.
 *
 * The first version of this portal gave a status a 6px dot and called it
 * restraint; the owner called it flat, and he was right - piles that differ by
 * a word are piles nobody can tell apart from across a desk. So each status has
 * an identity that is the same wherever it appears: the pile on the home page,
 * the pill on a card, the node on the concept's rail, the chip on the board.
 *
 * The colours themselves are not here. `tone` is the value of a `data-tone`
 * attribute, and `index.css` turns it into `--tone` / `--tone-ink` for light
 * and dark; components below it paint with `bg-tone/10`, `text-tone-ink` and so
 * on. Teal is the strategist's desk, red
 * is what came back, slate has not begun, yellow is on the editor's desk (the
 * owner asked for yellow by name), violet is delivered and back with the
 * strategist, magenta is the admin's last word, green is done and grey is gone.
 */
export const STATUS_ICON: Record<Status, LucideIcon> = {
  strategist: NotebookPenIcon,
  corrections: RotateCcwIcon,
  todo: CircleDashedIcon,
  doing: TimerIcon,
  review: EyeIcon,
  admin: ShieldCheckIcon,
  done: CheckCircle2Icon,
  trash: Trash2Icon,
};

/** Spread onto the element that owns the tone: `<section {...toneOf(status)}>`. */
export function toneOf(status: Status): { "data-tone": Status } {
  return { "data-tone": status };
}

/** The same, straight from a stage, for screens that think in stages (the board, the bell). */
export function stageTone(stage: PipelineStage): { "data-tone": Status } {
  return toneOf(statusOf(stage));
}

/**
 * The rail on the concept page: the places a concept can stand, in order.
 *
 * The editor's rail is four stops. The team's is six, because two of their
 * stops (the release and the admin's review) are the editor's "nothing to see
 * yet" and "delivered". "Needs revision" is not a stop on either: it is the
 * delivered stop having said no, and the rail draws it there, in red.
 * Cancelled is off the rail.
 */
const RAILS = {
  editor: ["todo", "doing", "review", "done"],
  team: ["strategist", "todo", "doing", "review", "admin", "done"],
} as const satisfies Record<Audience, readonly Status[]>;

export function railSteps(audience: Audience): readonly Status[] {
  return RAILS[audience];
}

/** Where on the rail a status stands, or null when it is not on it. */
export function railIndex(status: Status, audience: Audience): number | null {
  const steps: readonly Status[] = RAILS[audience];
  if (status === "trash") return null;
  if (status === "corrections") return steps.indexOf("review");
  const index = steps.indexOf(status);
  return index === -1 ? null : index;
}

/** The team's word for each status, under `pipeline.status`. */
const TEAM_KEY = {
  strategist: "pipeline.status.strategist",
  corrections: "pipeline.status.corrections",
  todo: "pipeline.status.todo",
  doing: "pipeline.status.doing",
  review: "pipeline.status.review",
  admin: "pipeline.status.admin",
  done: "pipeline.status.done",
  trash: "pipeline.status.trash",
} as const satisfies Record<Status, string>;

/** The editor's word for each of theirs, under `pipeline.editor.status`. */
export const STATUS_KEY = {
  corrections: "pipeline.editor.status.corrections",
  todo: "pipeline.editor.status.todo",
  doing: "pipeline.editor.status.doing",
  review: "pipeline.editor.status.review",
  done: "pipeline.editor.status.done",
  trash: "pipeline.editor.status.trash",
} as const satisfies Record<EditorStatus, string>;

const isEditorStatus = (status: Status): status is EditorStatus =>
  (EDITOR_STATUSES as readonly Status[]).includes(status);

/**
 * The words for a status, for this reader. Among the statuses both sides
 * share, one differs: "Ready for editor" is the editor's "Not started".
 */
export function statusLabelKey(status: Status, audience: Audience) {
  return audience === "editor" && isEditorStatus(status) ? STATUS_KEY[status] : TEAM_KEY[status];
}

/**
 * The piles' titles on the editor's home page, under `pipeline.editor.group`.
 *
 * Not the status words: every card in a pile already wears its status as a
 * pill, and a heading that says "In progress" over three pills that say "In progress"
 * is the same word four times. The pill names the status; the title says what
 * it means for the person reading.
 */
export const GROUP_KEY = {
  corrections: "pipeline.editor.group.corrections",
  todo: "pipeline.editor.group.todo",
  doing: "pipeline.editor.group.doing",
  review: "pipeline.editor.group.review",
  done: "pipeline.editor.group.done",
  trash: "pipeline.editor.group.trash",
} as const satisfies Record<EditorStatus, string>;

/**
 * Split an editor's flat list into the piles their home page draws, keeping
 * each pile in the order the server sent (by due date), and dropping the piles
 * that are empty rather than drawing a heading over nothing.
 */
export function groupByStatus<T extends { stage: PipelineStage }>(
  rows: T[],
): Array<{ status: EditorStatus; items: T[] }> {
  return EDITOR_STATUSES.map((status) => ({
    status,
    items: rows.filter((row) => editorStatusOf(row.stage) === status),
  })).filter((group) => group.items.length > 0);
}

/** One friendly sentence about where the concept stands, said to the editor. */
export const STATUS_HINT_KEY = {
  corrections: "pipeline.editor.statusHint.corrections",
  todo: "pipeline.editor.statusHint.todo",
  doing: "pipeline.editor.statusHint.doing",
  review: "pipeline.editor.statusHint.review",
  done: "pipeline.editor.statusHint.done",
  trash: "pipeline.editor.statusHint.trash",
} as const satisfies Record<EditorStatus, string>;

/** The same sentence said about the editor, to somebody looking across editors. */
export const TEAM_HINT_KEY = {
  strategist: "pipeline.overview.statusHint.strategist",
  corrections: "pipeline.overview.statusHint.corrections",
  todo: "pipeline.overview.statusHint.todo",
  doing: "pipeline.overview.statusHint.doing",
  review: "pipeline.overview.statusHint.review",
  admin: "pipeline.overview.statusHint.admin",
  done: "pipeline.overview.statusHint.done",
  trash: "pipeline.overview.statusHint.trash",
} as const satisfies Record<Status, string>;
